import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth, roleLabel, canManageOps, canConfigureTenant, canReview } from "@/lib/auth";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, ListChecks, ClipboardList, Building2, Users, ScrollText,
  BarChart3, Bell, LogOut, Menu, Shield, ChevronRight, MapPin, ClipboardCheck,
  History, Plus, Home as HomeIcon, QrCode, Clock, CreditCard, AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { api, setSubscriptionHandler } from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { toast } from "sonner";

function NavItem({ to, icon: Icon, label, testId, end = false, onClick }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      data-testid={testId}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 mx-3 rounded-xl transition-all duration-150 text-sm ${
          isActive
            ? "bg-primary text-primary-foreground font-medium shadow-lg shadow-primary/25"
            : "text-[hsl(var(--sidebar-muted))] hover:bg-white/5 hover:text-white"
        }`
      }
    >
      <Icon className="w-4 h-4 shrink-0" />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

function SidebarInner({ user, onLogout, onNavigate, notifCount, billingVisible }) {
  const isPlatform = user?.role === "platform_admin";
  const manage = canManageOps(user);
  const configure = canConfigureTenant(user);
  const review = canReview(user);
  return (
    <div
      className="h-full flex flex-col text-[hsl(var(--sidebar-foreground))]"
      style={{ background: "hsl(var(--sidebar))" }}
    >
      <div className="px-5 pt-6 pb-4" style={{ borderBottom: "1px solid hsl(var(--sidebar-border))" }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shadow-lg shadow-primary/30">
              <span className="text-primary-foreground font-display font-black text-lg">S</span>
          </div>
          <div>
            <div className="font-display font-bold text-lg leading-tight">Sparkle</div>
            <div className="text-[0.65rem] text-[hsl(var(--sidebar-muted))] mt-0.5">Operations OS</div>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 pb-4" style={{ borderBottom: "1px solid hsl(var(--sidebar-border))" }}>
        <div className="flex items-center gap-3">
          <Link
            to="/app/profile"
            onClick={onNavigate}
            className="flex items-center gap-3 min-w-0 flex-1 rounded-xl -mx-1 px-1 py-1 hover:bg-white/5 transition-colors"
            data-testid="sidebar-profile-link"
            title="Edit profile"
          >
            <Avatar className="w-10 h-10 border-2 border-white/10 shrink-0">
              <AvatarImage src={user?.photo_url} alt={user?.name} />
              <AvatarFallback className="bg-white/10 text-white">{user?.name?.[0] || "U"}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate text-white" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-[0.7rem] text-[hsl(var(--sidebar-muted))] truncate">{roleLabel(user?.role)}</div>
            </div>
          </Link>
          <button
            onClick={onLogout}
            className="p-2 rounded-lg hover:bg-white/10 text-[hsl(var(--sidebar-muted))] hover:text-white transition-colors shrink-0"
            data-testid="logout-btn"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto py-2 space-y-1">
        {user?.role !== "employee" && (
          <NavItem to="/app" end icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" onClick={onNavigate} />
        )}
        <NavItem to="/app/tasks" icon={ListChecks} label="Tasks" testId="nav-tasks" onClick={onNavigate} />
        {user?.role === "employee" && (
          <>
            <NavItem to="/app/my-tasks" icon={ClipboardList} label="My Tasks" testId="nav-my-tasks" onClick={onNavigate} />
            <NavItem to="/app/my-shift" icon={QrCode} label="My Shift" testId="nav-my-shift" onClick={onNavigate} />
          </>
        )}
        {review && (
          <NavItem to="/app/reviews" icon={ClipboardCheck} label="Reviews" testId="nav-reviews" onClick={onNavigate} />
        )}
        {review && user?.role !== "employee" && (
          <NavItem to="/app/people" icon={Users} label="People" testId="nav-people" onClick={onNavigate} />
        )}

        {configure && (
          <>
            <div className="px-6 mt-6 mb-2 text-[0.65rem] tracking-widest uppercase text-[hsl(var(--sidebar-muted))]">Configure</div>
            <NavItem to="/app/facilities" icon={MapPin} label="Facilities" testId="nav-facilities" onClick={onNavigate} />
            <NavItem to="/app/checklists" icon={ScrollText} label="Checklists" testId="nav-checklists" onClick={onNavigate} />
            {billingVisible && (
              <NavItem to="/app/billing" icon={CreditCard} label="Billing" testId="nav-billing" onClick={onNavigate} />
            )}
          </>
        )}

        {review && (
          <>
            <div className="px-6 mt-6 mb-2 text-[0.65rem] tracking-widest uppercase text-[hsl(var(--sidebar-muted))]">Insights</div>
            <NavItem to="/app/reports" icon={BarChart3} label="Reports" testId="nav-reports" onClick={onNavigate} />
            <NavItem to="/app/audit" icon={History} label="Audit Log" testId="nav-audit" onClick={onNavigate} />
          </>
        )}
        <NavLink
          to="/app/notifications"
          onClick={onNavigate}
          data-testid="nav-notifications"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 mx-3 rounded-xl transition-all duration-150 text-sm ${
              isActive
                ? "bg-primary text-primary-foreground font-medium shadow-lg shadow-primary/25"
                : "text-[hsl(var(--sidebar-muted))] hover:bg-white/5 hover:text-white"
            }`
          }
        >
          <Bell className="w-4 h-4 shrink-0" />
          <span className="truncate flex-1">Notifications</span>
          {notifCount > 0 && (
            <span className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-destructive text-[0.6rem] text-destructive-foreground flex items-center justify-center font-bold">
              {notifCount > 9 ? "9+" : notifCount}
            </span>
          )}
        </NavLink>

        {isPlatform && (
          <>
            <div className="px-6 mt-6 mb-2 text-[0.65rem] tracking-widest uppercase text-[hsl(var(--sidebar-muted))]">Platform</div>
            <NavItem to="/app/tenants" icon={Shield} label="Tenants" testId="nav-tenants" onClick={onNavigate} />
          </>
        )}
      </div>

    </div>
  );
}

function MobileBottomNav({ user, notifCount }) {
  const isEmployee = user?.role === "employee";
  const primaryHome = isEmployee ? "/app/my-tasks" : "/app";
  const items = isEmployee
    ? [
        { to: "/app/my-tasks", icon: ClipboardList, label: "Tasks", testId: "bnav-tasks" },
        { to: "/app/my-shift", icon: QrCode, label: "Shift", testId: "bnav-shift" },
        { to: "/app", icon: LayoutDashboard, label: "Home", testId: "bnav-home" },
        { to: "/app/notifications", icon: Bell, label: "Alerts", testId: "bnav-alerts", badge: notifCount },
      ]
    : [
        { to: "/app", icon: LayoutDashboard, label: "Home", testId: "bnav-home", end: true },
        { to: "/app/tasks", icon: ListChecks, label: "Tasks", testId: "bnav-tasks" },
        { to: "/app/reports", icon: BarChart3, label: "Reports", testId: "bnav-reports" },
        { to: "/app/notifications", icon: Bell, label: "Alerts", testId: "bnav-alerts", badge: notifCount },
      ];

  return (
    <nav
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border pt-2 pb-safe"
      style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
    >
      <div className="flex items-end justify-around px-2">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            data-testid={it.testId}
            className={({ isActive }) =>
              `relative flex flex-col items-center gap-1 px-3 py-1.5 min-w-[60px] ${
                isActive ? "text-primary" : "text-muted-foreground"
              }`
            }
          >
            <it.icon className="w-5 h-5" />
            <span className="text-[0.65rem] font-medium">{it.label}</span>
            {it.badge > 0 && (
              <span className="absolute top-0.5 right-1.5 min-w-4 h-4 px-1 rounded-full bg-destructive text-[0.55rem] text-destructive-foreground flex items-center justify-center font-bold">
                {it.badge > 9 ? "9+" : it.badge}
              </span>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [tenant, setTenant] = useState(null);
  const [billing, setBilling] = useState(null);
  const [platform, setPlatform] = useState(null);

  // Compute whether the Billing module should appear for THIS user:
  // 1. Platform billing flag must be ON globally.
  // 2. Tenant must still be `is_billable` (platform admin can opt a tenant out).
  // Platform admins never see the per-tenant Billing page (they have Tenants).
  const billingVisible = !!(
    platform?.billing_enabled &&
    user?.role !== "platform_admin" &&
    billing?.is_billable !== false &&
    billing?.status !== "disabled"
  );

  useEffect(() => {
    setSubscriptionHandler((detail) => {
      if (detail?.startsWith?.("plan_limit")) {
        const [, kind, limit] = detail.split(":");
        toast.error(`${kind} limit reached (${limit}). Upgrade your plan.`);
      } else {
        toast.error("Workspace locked — add a payment method to continue");
      }
      nav("/app/billing");
    });
    return () => setSubscriptionHandler(null);
  }, [nav]);

  useEffect(() => {
    if (!user) return;
    api.get("/platform/settings").then((r) => setPlatform(r.data)).catch(() => setPlatform({ billing_enabled: false }));
    const load = () => {
      api.get("/notifications").then((r) => {
        setNotifCount((r.data || []).filter((n) => !n.read).length);
      }).catch(() => {});
      if (user.tenant_id && user.role !== "platform_admin") {
        api.get("/billing/status").then((r) => setBilling(r.data)).catch(() => {});
      }
    };
    load();
    const t = setInterval(load, 20000);
    if (user.tenant_id) {
      api.get("/tenants/current").then((r) => setTenant(r.data)).catch(() => {});
    }
    return () => clearInterval(t);
  }, [user]);

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out");
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden md:block fixed inset-y-0 left-0 w-64 z-30">
        <SidebarInner user={user} onLogout={handleLogout} notifCount={notifCount} billingVisible={billingVisible} />
      </aside>

      {/* Top bar */}
      <header className="md:pl-64 sticky top-0 z-20 backdrop-blur-xl bg-background/85 border-b border-border">
        <div className="flex items-center justify-between px-4 md:px-8 h-16">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 border-0">
                <SidebarInner
                  user={user}
                  onLogout={handleLogout}
                  onNavigate={() => setOpen(false)}
                  notifCount={notifCount}
                  billingVisible={billingVisible}
                />
              </SheetContent>
            </Sheet>
            <div className="flex items-center gap-2 text-sm">
              {tenant && (
                <>
                  <span className="text-muted-foreground hidden sm:inline">Tenant</span>
                  <ChevronRight className="w-3 h-3 text-muted-foreground hidden sm:inline" />
                  <span className="font-semibold" data-testid="tenant-name">{tenant.name}</span>
                </>
              )}
              {user?.role === "platform_admin" && !tenant && (
                <Badge className="bg-primary text-primary-foreground">Platform</Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => nav("/app/notifications")}
              className="relative p-2 rounded-xl hover:bg-accent transition-colors"
              data-testid="notif-btn"
              aria-label="Notifications"
            >
              <Bell className="w-4 h-4" />
              {notifCount > 0 && (
                <span className="absolute top-1 right-1 min-w-4 h-4 px-1 rounded-full bg-destructive text-[0.6rem] text-destructive-foreground flex items-center justify-center font-bold">
                  {notifCount > 9 ? "9+" : notifCount}
                </span>
              )}
            </button>
            <Avatar className="w-9 h-9 border-2 border-primary/20 md:hidden">
              <AvatarImage src={user?.photo_url} alt={user?.name} />
              <AvatarFallback className="bg-primary/10 text-primary text-xs">{user?.name?.[0]}</AvatarFallback>
            </Avatar>
          </div>
        </div>
      </header>

      <main className="md:pl-64 min-h-[calc(100vh-4rem)] pb-24 md:pb-0">
        {billingVisible && billing && billing.status === "trialing" && billing.trial_days_left <= 3 && (
          <div className="bg-[hsl(38_92%_90%)] text-[hsl(38_92%_25%)] border-b border-[hsl(38_92%_70%)] px-4 md:px-8 py-2.5 text-sm flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              <span className="font-semibold">{billing.trial_days_left} day{billing.trial_days_left !== 1 ? "s" : ""} left in your free trial.</span>
              <span className="opacity-80 hidden sm:inline">Add a payment method before it ends to avoid lockout.</span>
            </div>
            <Button size="sm" className="rounded-full h-7 bg-[hsl(38_92%_35%)] text-white hover:bg-[hsl(38_92%_25%)]" onClick={() => nav("/app/billing")} data-testid="trial-cta">
              Choose a plan →
            </Button>
          </div>
        )}
        {billingVisible && billing && billing.status === "locked" && (
          <div className="bg-destructive/10 border-b border-destructive/40 px-4 md:px-8 py-2.5 text-sm flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-4 h-4" />
              <span className="font-semibold">Workspace locked.</span>
              <span className="opacity-80 hidden sm:inline">Choose a plan to resume operations.</span>
            </div>
            <Button size="sm" variant="destructive" className="rounded-full h-7" onClick={() => nav("/app/billing")} data-testid="locked-cta">
              Unlock now →
            </Button>
          </div>
        )}
        <Outlet />
      </main>

      <MobileBottomNav user={user} notifCount={notifCount} />
    </div>
  );
}
